import { isDefined } from "@clipboard-health/util-ts";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import {
  InfiniteScrollListbox,
  type InfiniteScrollListboxProps,
} from "@src/appV2/components/InfiniteScrollListbox";
import { useDebounce } from "@src/appV2/lib/utils";
import { FormFieldController } from "@src/appV2/PlacementCandidates/components/FormFieldController";
import { useGetRequirement } from "@src/appV2/Requirement/api/useGetRequirement";
import { useGetRequirements } from "@src/appV2/Requirement/api/useGetRequirements";
import { useEffect, useState } from "react";
import { type Control } from "react-hook-form";

import { type RequirementMappingFormValues } from "../types";

interface RequirementSelectFieldProps {
  requirementId?: string;
  control: Control<RequirementMappingFormValues>;
}

export function RequirementSelectField(props: RequirementSelectFieldProps) {
  const { control, requirementId } = props;
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedRequirement, setSelectedRequirement] = useState<
    { id: string; name: string } | undefined
  >(requirementId ? { id: requirementId, name: requirementId } : undefined);

  const debouncedSearchValue = useDebounce({ debounceTimeInMs: 500, value: searchValue });

  const { data: requirement, isSuccess } = useGetRequirement(requirementId);

  useEffect(() => {
    if (isSuccess) {
      setSelectedRequirement({ id: requirement.data.id, name: requirement.data.attributes.name });
    }
  }, [isSuccess, requirement]);

  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetRequirements(
    {
      filter: {
        archived: false,
        ...(debouncedSearchValue ? { name: debouncedSearchValue } : {}),
      },
    },
    { enabled: !isDefined(requirementId) }
  );

  const isInitialLoading = isFetching && !isFetchingNextPage;

  const listboxProps: InfiniteScrollListboxProps = {
    hasNextPage: Boolean(hasNextPage),
    loading: isFetchingNextPage,
    onLoadMore: fetchNextPage,
  };

  const requirements =
    data?.pages.flatMap((page) =>
      page.data.map((requirement) => ({ id: requirement.id, name: requirement.attributes.name }))
    ) ?? [];

  return (
    <FormFieldController
      required
      name="requirementId"
      control={control}
      label="Requirement"
      sx={{ mb: 2 }}
      render={(field) => (
        <Autocomplete
          options={requirements}
          loading={isInitialLoading}
          loadingText="Loading..."
          disabled={Boolean(requirementId)}
          inputValue={searchValue}
          value={selectedRequirement}
          getOptionLabel={(option) => option?.name ?? ""}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          // Disable client-side filtering; results are already filtered server-side
          filterOptions={(options) => options}
          ListboxComponent={InfiniteScrollListbox}
          ListboxProps={listboxProps}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              // @ts-expect-error - field not typed correctly
              // FIXME: Automatically disabled during ESLint config update
              error={Boolean(field.error)}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isInitialLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          onInputChange={(_, newInputValue) => {
            setSearchValue(newInputValue);
          }}
          onChange={(_, requirement) => {
            if (requirement) {
              setSelectedRequirement(requirement);
              field.onChange(requirement.id);
            } else {
              setSelectedRequirement(undefined);
              field.onChange("");
            }
          }}
        />
      )}
    />
  );
}
